import React from "react";

const AboutUs = () => {

  const useScrollToTop = () => {
    React.useEffect(() => {
      window.scrollTo(0, 0);
    }, []);
  }
  useScrollToTop();
  
  return (
    <div className="bg-gradient-to-b from-gray-50 to-gray-100 text-gray-800 font-sans min-h-screen">
      {/* Hero Section with Background */}
      <div className="bg-blue-600 text-white py-16 mb-8">
        <div className="max-w-4xl mx-auto px-6 md:px-8">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-center">About Formatally</h1>
          <p className="text-xl text-center text-blue-100 max-w-2xl mx-auto">
            Your all-in-one hub for powerful, reliable, and easy-to-use data tools
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-6 py-8 md:px-8">
        {/* Introduction with Card Style */}
        <section className="mb-12 bg-white rounded-lg shadow-md p-8">
          <h2 className="text-2xl font-semibold mb-4 text-blue-700 border-b border-gray-200 pb-2">Who We Are</h2>
          <p className="text-lg leading-relaxed">
            <strong className="text-blue-600">Formatally</strong> is your all-in-one online hub for powerful, reliable, and easy-to-use data tools. We specialize in data conversion, data formatting, and data encryption/decryption tools — helping individuals and professionals simplify complex tasks with just a few clicks.
          </p>
        </section>

        {/* Mission & Vision in a Two-Column Layout */}
        <div className="grid md:grid-cols-2 gap-8 mb-12">
          <section className="bg-white rounded-lg shadow-md p-8 hover:shadow-lg transition-shadow">
            <div className="flex items-center mb-4">
              <div className="bg-blue-100 rounded-full p-3 mr-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h2 className="text-2xl font-semibold text-blue-700">Our Mission</h2>
            </div>
            <p className="leading-relaxed">
              Our mission is to make data handling effortless and accessible for everyone. Whether you're a developer, analyst, student, or tech enthusiast, we aim to save your time by offering fast, accurate, and secure tools for your daily data manipulation needs.
            </p>
          </section>
          
          <section className="bg-white rounded-lg shadow-md p-8 hover:shadow-lg transition-shadow">
            <div className="flex items-center mb-4">
              <div className="bg-blue-100 rounded-full p-3 mr-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              </div>
              <h2 className="text-2xl font-semibold text-blue-700">Our Vision</h2>
            </div>
            <p className="leading-relaxed">
              We envision a digital ecosystem where everyone, regardless of technical skill, can process and transform data without hassle — all from a simple and intuitive interface.
            </p>
          </section>
        </div>

        {/* What We Offer with Features */}
        <section className="mb-12 bg-white rounded-lg shadow-md p-8">
          <h2 className="text-2xl font-semibold mb-4 text-blue-700 border-b border-gray-200 pb-2">What We Offer</h2>
          <p className="mb-6 text-lg">
            At <strong className="text-blue-600">www.formatally.com</strong>, you'll find over <strong>100+ tools</strong> designed to convert, format, and protect your data. Some of our popular tools include:
          </p>
          
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-blue-50 rounded-lg p-4 flex items-start">
              <div className="bg-blue-100 rounded-full p-2 mr-3">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <span>JSON to PDF Converter</span>
            </div>
            <div className="bg-blue-50 rounded-lg p-4 flex items-start">
              <div className="bg-blue-100 rounded-full p-2 mr-3">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <span>JSON to Excel Converter</span>
            </div>
            <div className="bg-blue-50 rounded-lg p-4 flex items-start">
              <div className="bg-blue-100 rounded-full p-2 mr-3">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <span>JSON Formatter</span>
            </div>
            <div className="bg-blue-50 rounded-lg p-4 flex items-start">
              <div className="bg-blue-100 rounded-full p-2 mr-3">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <span>XML Formatter</span>
            </div>
            <div className="bg-blue-50 rounded-lg p-4 flex items-start">
              <div className="bg-blue-100 rounded-full p-2 mr-3">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <span>Encrypt Data (AES, Base64, etc.)</span>
            </div>
            <div className="bg-blue-50 rounded-lg p-4 flex items-start">
              <div className="bg-blue-100 rounded-full p-2 mr-3">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <span>Decrypt Data</span>
            </div>
          </div>
          
          <div className="mt-6 text-center">
            <span className="px-6 py-2 bg-blue-600 text-white rounded-full inline-block font-medium">And 100+ more tools tailored for developers and data users</span>
          </div>
        </section>

        {/* Who It's For */}
        <section className="mb-12 bg-white rounded-lg shadow-md p-8">
          <h2 className="text-2xl font-semibold mb-4 text-blue-700 border-b border-gray-200 pb-2">Who Is It For?</h2>
          <p className="mb-6">
            Formatally is built for:
          </p>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:bg-blue-50 transition-colors">
              <div className="bg-blue-100 p-4 rounded-full mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                </svg>
              </div>
              <h3 className="font-medium text-lg mb-2">Developers</h3>
              <p className="text-center text-gray-600">Needing quick data transformations</p>
            </div>
            
            <div className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:bg-blue-50 transition-colors">
              <div className="bg-blue-100 p-4 rounded-full mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h3 className="font-medium text-lg mb-2">Data Analysts</h3>
              <p className="text-center text-gray-600">Working with complex file formats</p>
            </div>
            
            <div className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:bg-blue-50 transition-colors">
              <div className="bg-blue-100 p-4 rounded-full mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <h3 className="font-medium text-lg mb-2">Students & Educators</h3>
              <p className="text-center text-gray-600">Learning data structures</p>
            </div>
            
            <div className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:bg-blue-50 transition-colors">
              <div className="bg-blue-100 p-4 rounded-full mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h3 className="font-medium text-lg mb-2">Security Conscious</h3>
              <p className="text-center text-gray-600">Needing to encrypt/decrypt sensitive data</p>
            </div>
          </div>
        </section>

        {/* Our Commitment */}
        <section className="mb-12 bg-blue-600 text-white rounded-lg shadow-md p-8">
          <h2 className="text-2xl font-semibold mb-4 border-b border-blue-500 pb-2">Our Commitment</h2>
          <p className="text-lg leading-relaxed">
            We are committed to keeping our tools free, accurate, and up-to-date. Your data security and productivity are our top priorities. With zero log policies and client-side operations for sensitive tools, we take your privacy seriously.
          </p>
        </section>

        {/* Closing Note */}
        <section className="text-center mb-16 px-4 py-12 bg-white rounded-lg shadow-md">
          <div className="mx-auto max-w-xl">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-blue-600 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="font-semibold text-xl mb-4">
              Thank you for choosing <span className="text-blue-600">Formatally</span>.  
            </p>
            <p className="text-gray-600 mb-6">We're excited to help you format, convert, and secure your data — faster than ever.</p>
            <p className="text-blue-700 font-bold text-2xl">Have a great day!</p>
          </div>
        </section>
      </div>
      
    </div>
  );
};

export default AboutUs;